import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, Loader2, ChevronRight, Plus, Trash2, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth, useBusiness } from "../App";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Assistant = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedBusiness } = useBusiness();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const businessId = selectedBusiness?.id;

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/assistant/sessions`, { headers });
      if (res.ok) setSessions(await res.json());
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    }
  }, [token]);

  useEffect(() => { if (token) fetchSessions(); }, [token, fetchSessions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadSession = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/assistant/sessions/${sessionId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setActiveSession(sessionId);
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error("Failed to load session:", err);
    }
  };

  const startNewChat = () => {
    setActiveSession(null);
    setMessages([]);
    setInput("");
  };

  const deleteSession = async (sid) => {
    try {
      await fetch(`${API_URL}/api/assistant/sessions/${sid}`, { method: "DELETE", headers });
      setSessions(prev => prev.filter(s => s.id !== sid));
      if (activeSession === sid) startNewChat();
    } catch (err) {
      console.error("Failed to delete session:", err);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/assistant/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({ message: text, session_id: activeSession, business_id: businessId }),
      });
      const data = await res.json();
      if (res.ok) {
        if (!activeSession) {
          setActiveSession(data.session_id);
          fetchSessions();
        }
        setMessages(prev => [...prev, { role: "assistant", content: data.response, actions: data.actions, timestamp: new Date().toISOString() }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: `Error: ${data.detail || "Something went wrong"}` }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Connection failed. Please try again." }]);
    }
    setLoading(false);
  };

  const executeAction = async (action) => {
    if (action.type === "navigate") {
      const pageMap = { dashboard: "/dashboard", bookkeeping: "/bookkeeping", "tax-calculator": "/tax-calculator", deductions: "/deductions", "cash-flow": "/cash-flow", "profit-loss": "/profit-loss", allocations: "/allocations" };
      navigate(pageMap[action.page] || "/dashboard");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/assistant/execute-action`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action, business_id: businessId }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessages(prev => [...prev, { role: "assistant", content: `Done! ${data.type === "rule" ? "Categorization rule created successfully." : data.type === "transaction" ? "Transaction added successfully." : "Action completed."}` }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Failed to execute action." }]);
    }
  };

  const renderContent = (text) => {
    const clean = text.replace(/```ACTION[\s\S]*?```/g, "").trim();
    return clean.split("\n").map((line, i) => {
      if (line.startsWith("###")) return <p key={i} className="font-semibold text-[#1e3a5f] mt-3 mb-1">{line.replace(/^#+\s*/, "")}</p>;
      if (line.startsWith("## ")) return <p key={i} className="font-bold text-[#1e3a5f] mt-3 mb-1 text-base">{line.replace(/^#+\s*/, "")}</p>;
      if (line.startsWith("- ") || line.startsWith("* ")) return <p key={i} className="text-sm text-[#3d4f63] ml-4 mb-0.5">&bull; {line.slice(2)}</p>;
      if (line.match(/^\d+\./)) return <p key={i} className="text-sm text-[#3d4f63] ml-4 mb-0.5">{line}</p>;
      if (!line.trim()) return <br key={i} />;
      return <p key={i} className="text-sm text-[#3d4f63] mb-1 leading-relaxed">{line}</p>;
    });
  };

  const actionLabel = (a) => {
    if (a.type === "create_rule") return `Create rule: "${a.pattern}" → ${a.category}`;
    if (a.type === "add_transaction") return `Add: ${a.description} ($${a.amount})`;
    if (a.type === "navigate") return `Go to ${a.page}`;
    return "Execute";
  };

  return (
    <div className="flex h-[calc(100vh-64px)]" data-testid="assistant-page">
      {/* Sessions Sidebar */}
      <div className="w-64 bg-white border-r border-[#e4e8ee] flex flex-col">
        <div className="p-4 border-b border-[#e4e8ee]">
          <button onClick={startNewChat} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#2d5a8e] transition-colors" data-testid="new-chat-btn">
            <Plus size={16} /> New Chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.map((s) => (
            <div key={s.id} className={`group flex items-center justify-between px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${activeSession === s.id ? "bg-[#1e3a5f]/10 text-[#1e3a5f] font-medium" : "text-[#5a6577] hover:bg-[#f5f7fa]"}`}
              onClick={() => loadSession(s.id)} data-testid={`session-${s.id}`}>
              <span className="truncate flex-1">{s.title || "New conversation"}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }} className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-500 transition-all" data-testid={`delete-session-${s.id}`}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {sessions.length === 0 && (
            <p className="text-xs text-[#8b95a5] text-center py-6">No conversations yet</p>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-[#f5f7fa]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-testid="assistant-messages">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-16 h-16 rounded-2xl bg-[#1e3a5f]/10 flex items-center justify-center mb-4">
                <MessageCircle size={28} className="text-[#1e3a5f]" />
              </div>
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Opturna AI Assistant</h2>
              <p className="text-sm text-[#5a6577] mb-6 text-center max-w-md">
                I have access to your business data. Ask me about taxes, bookkeeping, expenses, or deductions — and I can take action for you.
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-lg">
                {[
                  "How is my business health right now?",
                  "What tax deductions am I missing?",
                  "What should I ask my CPA before tax season?",
                  "How can I improve my profit margin?",
                ].map((q) => (
                  <button key={q} onClick={() => setInput(q)} className="text-left text-sm px-4 py-3 rounded-xl bg-white text-[#1e3a5f] hover:bg-[#1e3a5f]/5 transition-colors border border-[#e4e8ee]" data-testid="suggested-question">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === "user" ? "bg-[#1e3a5f] text-white" : "bg-white border border-[#e4e8ee]"}`}>
                    {msg.role === "user" ? (
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                    ) : (
                      <>
                        {renderContent(msg.content)}
                        {msg.actions?.length > 0 && (
                          <div className="mt-3 space-y-2 border-t border-[#e4e8ee] pt-3">
                            <p className="text-xs font-semibold text-[#1e3a5f]">Quick Actions:</p>
                            {msg.actions.map((a, j) => (
                              <button key={j} onClick={() => executeAction(a)} className="flex items-center gap-2 w-full text-left text-sm px-3 py-2 rounded-lg bg-[#1e3a5f] text-white hover:bg-[#2d5a8e] transition-colors" data-testid={`action-btn-${i}-${j}`}>
                                <ChevronRight size={14} />
                                {actionLabel(a)}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#e4e8ee] rounded-2xl px-4 py-3 flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-[#1e3a5f]" />
                    <span className="text-sm text-[#5a6577]">Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-6 py-4 border-t border-[#e4e8ee] bg-white">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Ask about your taxes, expenses, deductions..."
              className="flex-1 text-sm px-4 py-3 rounded-xl bg-[#f5f7fa] border border-[#e4e8ee] text-[#1e3a5f] placeholder-[#8b95a5] focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
              data-testid="assistant-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="p-3 rounded-xl bg-[#1e3a5f] text-white hover:bg-[#2d5a8e] transition-colors disabled:opacity-40"
              data-testid="assistant-send-btn"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assistant;
